import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Heart, Bed, Bath, Home, X, ArrowUpDown, Loader2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Button } from '../ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useLocalizedNavigation } from '../../hooks/useLocalizedNavigation';
import { PropertyListSidebarProps, Property, SortOption } from '../../types';

interface CompactPropertyCardProps {
  property: Property;
  isSelected: boolean;
  isHovered: boolean;
  onSelect: (id: string) => void;
  onHover: (id: string | null) => void;
}

const CompactPropertyCard: React.FC<CompactPropertyCardProps> = ({
  property,
  isSelected,
  isHovered,
  onSelect,
  onHover,
}) => {
  const { t } = useTranslation();
  const { user, toggleSaveProperty, isPropertySaved } = useAuth();
  const { getTextInCurrentLanguage } = useLanguage();
  const { getLocalizedPath } = useLocalizedNavigation();
  const isSaved = isPropertySaved(property.id);

  const formatPrice = (price: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const handleSaveClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (user) {
      toggleSaveProperty(property.id);
    }
  };

  const handleCardClick = () => {
    // Don't prevent default - we want the link to work
    onSelect(property.id);
  };

  return (
    <div
      data-property-id={property.id}
      className={`group relative border-b border-gray-100 transition-all cursor-pointer ${
        isSelected ? 'bg-blue-50 border-l-4 border-l-blue-600' : ''
      } ${isHovered && !isSelected ? 'bg-gray-50' : ''}`}
      onMouseEnter={() => onHover(property.id)}
      onMouseLeave={() => onHover(null)}
      onClick={handleCardClick}
    >
      <Link to={getLocalizedPath(`/property/${property.id}`)} className="block p-3">
        <div className="flex gap-3">
          {/* Image */}
          <div className="relative w-24 h-20 flex-shrink-0 rounded-lg overflow-hidden">
            <img
              src={property.mainImage}
              alt={getTextInCurrentLanguage(property.title)}
              className="w-full h-full object-cover"
            />
            {user && (
              <Button
                onClick={handleSaveClick}
                variant="ghost"
                size="icon"
                className={`absolute top-1 right-1 h-6 w-6 rounded-full bg-white/90 hover:bg-white ${
                  isSaved ? 'text-red-500' : 'text-gray-600'
                }`}
              >
                <Heart className={`h-3 w-3 ${isSaved ? 'fill-current' : ''}`} />
              </Button>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h3 className="text-lg font-bold text-gray-900">
                {formatPrice(property.price)}
              </h3>
            </div>

            {/* Stats */}
            <div className="flex items-center gap-2 text-xs text-gray-600 mt-1">
              <span className="flex items-center gap-0.5">
                <Bed className="h-3 w-3" />
                {property.beds}
              </span>
              <span className="flex items-center gap-0.5">
                <Bath className="h-3 w-3" />
                {property.baths}
              </span>
              <span className="flex items-center gap-0.5">
                <Home className="h-3 w-3" />
                {property.sqft.toLocaleString()} {t('common.sqft')}
              </span>
            </div>

            {/* Address */}
            <p className="text-xs text-gray-500 mt-1 truncate">
              {property.address}, {property.city}
            </p>
          </div>
        </div>
      </Link>
    </div>
  );
};

const PropertyListSidebar: React.FC<PropertyListSidebarProps> = ({
  properties,
  selectedPropertyId,
  hoveredPropertyId,
  onPropertySelect,
  onPropertyHover,
  loading,
  sortBy,
  onSortChange,
  onClose,
}) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-white">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-gray-900">
            {properties.length} {properties.length === 1 ? t('common.home') : t('common.homes')}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {/* Sort Dropdown */}
          <div className="flex items-center gap-1">
            <ArrowUpDown className="h-3 w-3 text-gray-500" />
            <Select value={sortBy} onValueChange={(value: SortOption) => onSortChange(value)}>
              <SelectTrigger className="w-[130px] h-8 text-xs border-0 shadow-none">
                <SelectValue placeholder={t('filters.sortBy')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">{t('filters.sortNewest')}</SelectItem>
                <SelectItem value="price-low">{t('filters.sortPriceLow')}</SelectItem>
                <SelectItem value="price-high">{t('filters.sortPriceHigh')}</SelectItem>
                <SelectItem value="beds">{t('filters.sortBeds')}</SelectItem>
                <SelectItem value="sqft">{t('filters.sortSqft')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Close Button */}
          <Button
            onClick={onClose}
            variant="ghost"
            size="icon"
            className="h-8 w-8"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Property List */}
      <div className="flex-1 overflow-y-auto">
        {properties.length === 0 ? (
          <div className="flex items-center justify-center h-full text-center p-6">
            <div>
              <p className="text-gray-500 text-sm">{t('search.noPropertiesFound')}</p>
              <p className="text-gray-400 text-xs mt-1">{t('search.tryAdjusting')}</p>
            </div>
          </div>
        ) : (
          properties.map((property) => (
            <CompactPropertyCard
              key={property.id}
              property={property}
              isSelected={selectedPropertyId === property.id}
              isHovered={hoveredPropertyId === property.id}
              onSelect={onPropertySelect}
              onHover={onPropertyHover}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default PropertyListSidebar;
